import { useQuery } from '@apollo/client';
import { Strategy } from '@interfaces/financialProduct';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { FETCH_THEMES } from '@pages/buildModels/buildModels.queries';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { calculateDurationSeconds } from '@utils/analytics/eventMetrics';
import { useContext, useEffect, useRef, useState } from 'react';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';
import { AddModelPortfolioDetailsVisual } from './addModelPortfolioDetails.visual';

export type AddModelPortfolioDetailsProps = {
  options: {
    [key: string]: any;
  };
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: {
    [key: string]: any;
  };
};

export const AddModelPortfolioDetails = ({
  options,
  onNext,
  stepLoading,
  workflowCompletion,
}: AddModelPortfolioDetailsProps) => {
  const { activeOrganization } = useContext(UserContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { workflowData, setWorkflowData, updateWorkflowContext } =
    useContext(WorkflowContext);
  const stepStartTime = useRef<Date | null>(null);
  const [modelPortfolioData, setModelPortfolioData] =
    useState<ModelPortfolioData>({
      id: workflowData.modelPortfolioDetails?.id ?? '',
      strategy:
        workflowData.modelPortfolioDetails?.strategy ?? Strategy.STANDARD,
      themeId: workflowData.modelPortfolioDetails?.themeId ?? '',
      translatedName:
        workflowData.modelPortfolioDetails?.translatedName ??
        getLanguageDefaults(),
      translatedPortfolioDescription:
        workflowData.modelPortfolioDetails?.translatedPortfolioDescription ??
        getLanguageDefaults(),
      riskProfileName:
        workflowData.modelPortfolioDetails?.riskProfileName ??
        getLanguageDefaults(),
      portfolioTemplate:
        workflowData.modelPortfolioDetails?.portfolioTemplate ?? null,
      securities: workflowData.modelPortfolioDetails?.securities ?? [],
      translatedUrl:
        workflowData.modelPortfolioDetails?.translatedUrl ??
        getLanguageDefaults(),
      ...workflowData.modelPortfolioDetails,
      suggestable:
        workflowData.modelPortfolioDetails?.suggestable !== undefined
          ? workflowData.modelPortfolioDetails.suggestable
          : (options?.suggestable?.enabled ?? false),
      templateCreationInProgress: false,
    });
  const [originalValues, setOriginalValues] = useState<ModelPortfolioData>(
    workflowData.modelPortfolioDetails?.originalValues || modelPortfolioData,
  );
  const { saveModelPortfolio, loading } = useSaveModelPortfolio();

  // Track step start time when component mounts
  useEffect(() => {
    stepStartTime.current = new Date();
  }, []);

  useEffect(() => {
    setWorkflowData({
      ...workflowData,
      modelPortfolioDetails: {
        ...modelPortfolioData,
        originalValues,
      },
    });
  }, [modelPortfolioData, originalValues, workflowData, setWorkflowData]);

  const { data: themesData } = useQuery(FETCH_THEMES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
        },
        pagination: {
          perPage: 25,
          sortField: 'createdAt',
          sortDesc: false,
        },
      },
    },
    skip: !activeOrganization?.id,
  });

  const handleContinue = async (
    updatedModelPortfolioData: ModelPortfolioData,
  ) => {
    try {
      setModelPortfolioData(updatedModelPortfolioData);
      await setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: {
          ...updatedModelPortfolioData,
          originalValues,
        },
      });

      if (updateWorkflowContext) {
        await updateWorkflowContext();
      }

      // Calculate duration and send analytics before proceeding to next step
      const stepEndTime = new Date();
      const formDuration = calculateDurationSeconds(stepStartTime.current, {
        endTime: stepEndTime,
      });

      sendAnalytic(ovAnalyticsEvents.modelPortfolioWorkflowStep2Complete, {
        stepNumber: 2,
        stepName: 'ADD_MODEL_PORTFOLIO_DETAILS',
        formDuration,
        timestamp: stepEndTime.toISOString(),
        workflowCompletionId: workflowCompletion?.id,
      });

      onNext();
    } catch {
      /* empty */
    }
  };

  const handleSaveForLater = async (
    updatedModelPortfolioData: ModelPortfolioData,
  ) => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(
        updatedModelPortfolioData,
      );

      const savedData = {
        ...savedModelPortfolioData,
        originalValues: savedModelPortfolioData,
      };

      setModelPortfolioData(savedData);
      setOriginalValues(savedModelPortfolioData);
    } catch {
      // Error is already handled by saveModelPortfolio
    }
  };

  return (
    <AddModelPortfolioDetailsVisual
      modelPortfolioData={modelPortfolioData}
      setModelPortfolioData={setModelPortfolioData}
      originalValues={originalValues}
      options={options}
      loading={stepLoading || loading}
      continueFunc={handleContinue}
      onSaveForLater={handleSaveForLater}
      themes={themesData?.fetchThemes?.themes ?? []}
      workflowCompletion={workflowCompletion}
    />
  );
};

export default AddModelPortfolioDetails;
